import * as React from "react"

import { Role } from "@/lib/roles"
import {
  connectReview,
  type ConnectionStatus,
  type InboundFrame,
} from "@/lib/ws"

import type { LiveState, ReviewId, UserId } from "../data/review-data"

/**
 * Realtime channel for a single review. Transport is `lib/ws.ts`; this
 * module is the public consumer surface for the workspace.
 *
 * Contract:
 *   • `status` is the authoritative three-state liveness
 *     (`live` / `reconnecting` / `offline`) that the header indicator
 *     renders. A socket being open is not evidence of end-to-end
 *     delivery — but is a reasonable proxy for it in this app.
 *   • `presence` is a role-only projection of who else is looking at
 *     the review. Names are never broadcast to client channels, because
 *     staff identity is confidential in the counterparty view.
 *   • `cursors` carry PDF-point coordinates so remote cursors track the
 *     document at any zoom without recomputation. Entries are keyed by
 *     `userId`; a late-arriving frame from a user who has already
 *     departed cannot resurrect them.
 *   • Invalidation frames dispatched through `useLiveInvalidation` are
 *     coalesced inside a short window so a burst of updates collapses
 *     to a single refetch.
 */

export interface PresenceViewer {
  userId: UserId
  role: string
}

export interface RemoteCursor {
  userId: UserId
  role: string
  page: number
  x: number
  y: number
  updatedAt: number
}

export interface UseReviewLiveResult {
  status: LiveState
  lastFrameAt: number | null
  presence: PresenceViewer[]
  cursors: RemoteCursor[]
}

const CURSOR_TTL_MS = 8_000

function toLiveState(status: ConnectionStatus): LiveState {
  if (status === "open") return "live"
  if (status === "connecting" || status === "reconnecting") return "reconnecting"
  return "offline"
}

export function useReviewLive(reviewId: ReviewId | null): UseReviewLiveResult {
  const [status, setStatus] = React.useState<LiveState>("offline")
  const [lastFrameAt, setLastFrameAt] = React.useState<number | null>(null)
  const [presence, setPresence] = React.useState<PresenceViewer[]>([])
  const [cursors, setCursors] = React.useState<RemoteCursor[]>([])

  React.useEffect(() => {
    if (!reviewId) {
      setStatus("offline")
      setPresence([])
      setCursors([])
      return
    }

    const connection = connectReview(reviewId)
    const offStatus = connection.onStatus((s) => setStatus(toLiveState(s)))
    const offFrame = connection.onFrame((frame: InboundFrame) => {
      setLastFrameAt(Date.now())
      if (frame.type === "presence") {
        // The backend sends `viewers` as a role-only string list (routes_ws.py
        // `_viewer_label`). Identities are withheld on purpose — a client-role
        // reader must not receive staff user_ids. We synthesize a stable
        // per-role key so React `key=` stays stable while multiple people of
        // the same role are present, using the index as a tiebreaker.
        setPresence(
          frame.viewers
            .filter((role): role is string => typeof role === "string" && role.length > 0)
            .map((role, i) => ({
              userId: `${role}-${String(i)}` as UserId,
              role,
            })),
        )
        return
      }
      if (frame.type === "cursor.moved") {
        const c = frame.cursor
        // Malformed frames get dropped rather than pushed as junk cursors.
        if (typeof c?.user_id !== "string" || c.user_id.length === 0) return
        setCursors((prev) => {
          const now = Date.now()
          const kept = prev.filter(
            (existing) =>
              existing.userId !== c.user_id &&
              now - existing.updatedAt < CURSOR_TTL_MS,
          )
          kept.push({
            userId: c.user_id as UserId,
            role: typeof c.role === "string" && c.role.length > 0 ? c.role : Role.VIEWER,
            page: typeof c.page === "number" ? c.page : 1,
            x: typeof c.x === "number" ? c.x : 0,
            y: typeof c.y === "number" ? c.y : 0,
            updatedAt: typeof c.updated_at === "number" ? c.updated_at : Date.now(),
          })
          return kept
        })
      }
    })

    return () => {
      offFrame()
      offStatus()
      connection.close()
    }
  }, [reviewId])

  return { status, lastFrameAt, presence, cursors }
}

const INVALIDATION_DEBOUNCE_MS = 250

/**
 * Subscribes a callback to invalidation frames for the given review.
 * Frames are debounced so a burst of `review.updated` events (a batch
 * edit from a colleague) collapses to a single refetch on the consumer.
 */
export function useLiveInvalidation(
  reviewId: ReviewId | null,
  onInvalidate: () => void,
): void {
  const callbackRef = React.useRef(onInvalidate)
  React.useEffect(() => {
    callbackRef.current = onInvalidate
  }, [onInvalidate])

  React.useEffect(() => {
    if (!reviewId) return
    const connection = connectReview(reviewId)
    let timer: ReturnType<typeof setTimeout> | null = null
    const off = connection.onFrame((frame) => {
      if (frame.type !== "review.updated" && frame.type !== "comment.created") {
        return
      }
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        timer = null
        callbackRef.current()
      }, INVALIDATION_DEBOUNCE_MS)
    })
    return () => {
      if (timer) clearTimeout(timer)
      off()
      connection.close()
    }
  }, [reviewId])
}
